import { useAuthStore } from "@/stores/authStore";
import axios, { InternalAxiosRequestConfig } from "axios";
import { refreshAccessToken } from "./auth";
import qs from "qs";
import { toast } from "@/hooks/use-toast";
import { t } from "i18next";
import { Sentry } from "@/sentry";

export const BASE_URL = import.meta.env.VITE_API_BASE_URL;

const api_base = axios.create({
  baseURL: BASE_URL,
});

// Throttle timestamp to prevent repeated toasts/redirects on 402
let lastPaymentRedirectAt = 0;
// Throttle timestamp for the pending-approval (403) redirect
let lastApprovalRedirectAt = 0;

const auth_interceptor = (
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig => {
  // Add Sentry breadcrumb for API requests
  Sentry.addBreadcrumb({
    category: 'api',
    message: `${config.method?.toUpperCase()} ${config.url}`,
    level: 'info',
    data: {
      url: config.url,
      method: config.method,
    },
  });

  if (config.url == "/api/v1/login") {
    return config;
  }
  const token = useAuthStore.getState().auth.accessToken;
  if (token) {
    // If the token exists, add it to the Authorization header
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
};
api_base.interceptors.request.use(auth_interceptor);

api_base.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 429 Too Many Requests error globally
    if (error.response?.status === 429) {
      toast({
        title: "طلبات كثيرة جداً",
        description: "يرجى المحاولة مرة أخرى بعد قليل",
        variant: "destructive",
      });
      return Promise.reject(error);
    }

    // Handle 402 Payment Required - redirect to cabinet plans
    if (error.response?.status === 402) {
      const currentPath = window.location.pathname;
      
      // Don't redirect if user is on any email verification related page
      // This includes /auth/verify-email and /auth/verify-email-confirm/*
      if (currentPath.startsWith('/auth/verify-email') || 
          currentPath.includes('verify-email')) {
        return Promise.reject(error);
      }
      
      // Don't redirect if user is on any auth page (login, signup, etc.)
      // as they might be in the process of authentication
      if (currentPath.startsWith('/auth/')) {
        return Promise.reject(error);
      }
      
      // Throttle redirects to prevent repeated actions
      const now = Date.now();
      if (now - lastPaymentRedirectAt < 5000) {
        return Promise.reject(error);
      }
      
      // Don't redirect if already on cabinet plans page
      if (currentPath !== '/cabinet-plans') {
        lastPaymentRedirectAt = now;
        
        toast({
          title: t("payment_required"),
          description: t("payment_required_desc"),
          variant: "destructive",
        });
        
        setTimeout(() => {
          window.location.href = `/cabinet-plans`;
        }, 2000);
      }
      
      return Promise.reject(error);
    }

    // Handle 403 signup-approval gate - cabinet awaiting super-admin approval
    if (error.response?.status === 403) {
      const code = (error.response.data as { code?: string })?.code;
      if (code === 'cabinet_pending_approval' || code === 'cabinet_rejected') {
        const currentPath = window.location.pathname;
        // Don't hijack navigation while on auth pages (signup handles its own routing)
        if (currentPath.startsWith('/auth/')) {
          return Promise.reject(error);
        }
        const now = Date.now();
        if (now - lastApprovalRedirectAt >= 5000 && currentPath !== '/auth/pending-approval') {
          lastApprovalRedirectAt = now;
          setTimeout(() => {
            window.location.href = `/auth/pending-approval`;
          }, 1500);
        }
        return Promise.reject(error);
      }
    }

    // Check if the error is due to an expired token
    if (
      error.config.url != "/api/v1/login" &&
      error.response?.status === 401 &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true; // Prevent infinite loop

      try {
        const newAccessToken = await refreshAccessToken();
        originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
        return axios(originalRequest); // Retry the original request
      } catch (refreshError) {
        // Only disconnect the user if the refresh token is denied by the server
        // This would happen if the refresh token is expired or invalid
        if (axios.isAxiosError(refreshError) && 
           (refreshError.response?.status === 401 || refreshError.response?.status === 403)) {
          const auth = useAuthStore.getState().auth;
          auth.reset();
          
          toast({
            title: t("session_expired"),
            description: t("session_expired_desc"),
            variant: "destructive",
          });
          
          setTimeout(() => {
            window.location.href = `/auth/login`;
          }, 5000);
        }
        
        return Promise.reject(refreshError);
      }
    }

    // Capture unhandled API errors to Sentry
    // Only capture errors that weren't already handled (401, 403, 429, 402)
    if (error.response && ![401, 403, 429, 402].includes(error.response.status)) {
      Sentry.captureException(error, {
        tags: {
          api_error: true,
          status_code: error.response?.status,
        },
        contexts: {
          response: {
            status: error.response?.status,
            data: error.response?.data,
          },
          request: {
            url: error.config?.url,
            method: error.config?.method,
          },
        },
      });
    }

    return Promise.reject(error);
  }
);

type Params = {
  [key: string]:
    | string
    | string[]
    | number
    | number[]
    | boolean
    | boolean[]
    | null
    | undefined;
};

export const serializeParams = (params: Params): string => {
  return qs.stringify(
    Object.fromEntries(
      Object.entries(params)
        .map(([key, value]) => {
          if (Array.isArray(value)) {
            return [key, value.filter((v) => v != null)];
          }
          return [key, value != null ? value : undefined];
        })
        .filter(([_, value]) => value != null)
    ),
    { arrayFormat: "comma" }
  );
};

export default api_base;
